/**
 * Owner scoping for agent sessions — the one question every surface that
 * reaches an agent has to answer, answered once.
 *
 * Agent sessions are the user-owned kind both the host and most plugins
 * gate: sandbox shell, sandbox filesystem, axon-viewer, Slack. Each was
 * building the `{ type, id, ownerId, permissions }` literal by hand and
 * wrapping it in its own private `checkAgentAccess`. That literal feeds an
 * authorization decision, so N copies is N chances for one to drift into a
 * different answer about who owns a session or which slug gates it.
 *
 * {@link checkAgentAccess} is the shared default. A plugin gating an agent
 * calls it with the `authz` service it registered with — there is nothing
 * per-plugin left to get wrong.
 */

import {
  ownedResourcePermissions,
  checkOwnedResourceAccess,
  type AuthzCaller,
  type OwnedResourceAccess,
  type OwnedResourceAuthz,
  type OwnedResourceMode,
} from './owned-resource.js';

/** Resource type recorded on per-agent grants (`resource_grants.resource_type`). */
export const AGENT_RESOURCE_TYPE = 'agent';

/** Access slugs an agent session is reached under, by mode (`read` / `write`). */
export const AGENT_RESOURCE_PERMISSIONS = ownedResourcePermissions('agents');

/** Access mode a surface performs on an agent session. */
export type AgentAccessMode = OwnedResourceMode<typeof AGENT_RESOURCE_PERMISSIONS>;

/**
 * The fields an access decision needs about an agent session: which one, and
 * who owns it. Deliberately structural — every surface has its own row shape
 * (`Agent`, `DevboxAgentRef`, a Slack thread's target), and none of them
 * needs to be imported to ask this question.
 */
export interface AgentAccessTarget {
  id: string;
  /**
   * The authoritative owner from `agents.owner_id`: a user for a personal
   * run, the organization for an org-owned one, or a service account.
   * Deliberately NOT the `userId` projection — that reads `null` for an
   * org-owned run, which the check would fail closed on, denying every
   * member a session the whole org shares. Optional/nullable only because
   * surfaces differ in shape; an absent owner is an anomaly and fails
   * closed.
   */
  ownerId?: string | null;
}

/**
 * Build the owned-resource reference for an agent session.
 *
 * Generic in the owner so both callers keep their shape: a surface that has
 * already resolved a real owner gets an `OwnedResource` it can pass straight
 * to `canAccessOwnedResource`, while a devbox surface holding a nullable
 * owner gets the nullable form `checkOwnedResourceAccess` accepts and fails
 * closed on. A null owner is an anomaly, never an org-shared fallback.
 */
export function agentOwnedResource<Owner extends string | null>(agent: {
  id: string;
  ownerId: Owner;
}): {
  type: string;
  id: string;
  ownerId: Owner;
  permissions: typeof AGENT_RESOURCE_PERMISSIONS;
} {
  return {
    type: AGENT_RESOURCE_TYPE,
    id: agent.id,
    ownerId: agent.ownerId,
    permissions: AGENT_RESOURCE_PERMISSIONS,
  };
}

/**
 * Whether `caller` may act on `agent` in `mode`, on top of the base
 * `agents:read` / `agents:write` slug the surface has already enforced.
 *
 * Org-shared sessions (service-account-owned) and the caller's own pass;
 * any other session needs a per-agent grant on that exact agent. There is
 * no role path and no platform-admin path — an operator reaches a user's
 * session by being granted it.
 *
 * The host's equivalent is `canAccessAgent` in
 * `server/src/auth/agent-owner-scope.ts`, which resolves the same rule
 * straight off `AuthorizationService`; both bottom out in
 * `canOnResource`.
 */
export function checkAgentAccess(
  authz: OwnedResourceAuthz,
  caller: AuthzCaller,
  agent: AgentAccessTarget,
  mode: AgentAccessMode,
): Promise<OwnedResourceAccess> {
  return checkOwnedResourceAccess(
    authz,
    caller,
    agentOwnedResource({ id: agent.id, ownerId: agent.ownerId ?? null }),
    mode,
  );
}
